import React from 'react';
import { motion } from 'framer-motion';
import { PageLayout } from '../components/layout/Navbar';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { getDisplayName } from '../utils/userDisplay';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFleetStatus } from '../hooks/useFleetStatus';
import { useOrderSocket } from '../hooks/useOrderSocket';
import { ORDER_STATUS, FLEET_STATUS_MESSAGE } from '../constants';

export default function MainPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { fleet, loading, refresh } = useFleetStatus(15000);

  useOrderSocket({ onRefresh: refresh });

  const inQueue = fleet?.userOrder?.status === ORDER_STATUS.PENDING;
  const myPosition = fleet?.queuePosition?.position;
  const activeRoute = fleet?.activeRoute;
  const fleetHint = fleet?.message ? FLEET_STATUS_MESSAGE[fleet.message] : null;

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center sg-user-app">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="sg-h1 text-[32px]">
            {t('mainPage.hello')}, <span className="text-[var(--brand-green)]">{getDisplayName(user).split(' ')[0] || getDisplayName(user)}!</span>
          </h1>
          <p className="text-[var(--fg3)] font-bold mt-1.5 text-sm uppercase tracking-[0.12em] italic">
            {t('mainPage.today')}: {new Date().toLocaleDateString('uz-UZ', { day: 'numeric', month: 'long' })}
          </p>
        </div>

        {fleetHint && !activeRoute && (
          <Card className="p-4 mb-6 rounded-2xl bg-amber-50 border-amber-200">
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 text-center">{fleetHint}</p>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-4 mb-5">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(activeRoute ? '/tracking' : '/navbat')}
            className="h-[152px] bg-[var(--bg-surface)] border-4 border-[var(--accent-blue)] rounded-[var(--radius-2xl)] shadow-lg hover:shadow-2xl flex flex-col items-center justify-center gap-3.5 relative overflow-hidden p-3"
          >
            <div className="h-16 w-16 bg-[var(--accent-blue)] text-white rounded-[20px] flex items-center justify-center shadow-lg">
              <span className="material-symbols-outlined text-[36px]" style={{ fontVariationSettings: "'FILL' 1" }}>location_on</span>
            </div>
            <span className="text-[19px] font-black text-[var(--fg1)] italic uppercase tracking-tight text-center leading-tight">{t('mainPage.whereIsGas')}</span>
            {activeRoute && (
              <Badge variant="blue" className="absolute top-6 right-6 animate-pulse font-black uppercase text-[10px]">
                {activeRoute.status}
              </Badge>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/navbat')}
            className={`h-[152px] rounded-[var(--radius-2xl)] shadow-lg hover:shadow-2xl flex flex-col items-center justify-center gap-3.5 border-4 p-3 ${
              inQueue ? 'bg-[var(--brand-green)] border-[var(--brand-green)] text-white' : 'bg-[var(--bg-surface)] border-[var(--brand-green)]'
            }`}
          >
            <div className={`h-16 w-16 rounded-[20px] flex items-center justify-center shadow-lg ${
              inQueue ? 'bg-white text-[var(--brand-green)]' : 'bg-[var(--brand-green)] text-white'
            }`}>
              <span className="material-symbols-outlined text-[36px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                {inQueue ? 'check_circle' : 'add_shopping_cart'}
              </span>
            </div>
            <span className={`text-[19px] font-black italic uppercase tracking-tight text-center leading-tight ${inQueue ? 'text-white' : 'text-[var(--fg1)]'}`}>
              {inQueue && myPosition ? `${t('mainPage.yourQueue')}: #${myPosition}` : t('mainPage.book')}
            </span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/tolov')}
            className="h-[152px] bg-[var(--bg-surface)] border-4 border-[var(--accent-amber)] rounded-[var(--radius-2xl)] shadow-lg hover:shadow-2xl flex flex-col items-center justify-center gap-3.5 p-3"
          >
            <div className="h-16 w-16 bg-[var(--accent-amber)] text-white rounded-[20px] flex items-center justify-center shadow-lg">
              <span className="material-symbols-outlined text-[36px]" style={{ fontVariationSettings: "'FILL' 1" }}>account_balance_wallet</span>
            </div>
            <div className="text-center">
              <span className="text-[19px] font-black text-[var(--fg1)] italic uppercase tracking-tight block">{t('mainPage.balance')}</span>
              <span className="text-[15px] font-bold text-[var(--brand-green)] italic">{(user?.balance ?? 0).toLocaleString()} so&apos;m</span>
            </div>
          </motion.button>
        </div>

        {activeRoute && (
          <Card className="p-6 bg-gradient-to-br from-sky-400 to-[var(--accent-blue)] text-white border-0 rounded-[var(--radius-xl)]">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="h-16 w-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <span className="material-symbols-outlined text-[32px] animate-bounce">local_shipping</span>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-black uppercase tracking-widest">{t('mainPage.activeDelivery')}</p>
                  <h3 className="text-2xl font-black italic">{t('mainPage.gasDelivering')}</h3>
                  <p className="text-white/80 font-bold mt-1 text-sm uppercase">{activeRoute.status}</p>
                </div>
              </div>
              <Button
                variant="secondary"
                className="rounded-2xl px-10 h-14 font-black uppercase bg-white text-blue-600"
                onClick={() => navigate('/tracking')}
              >
                {t('mainPage.viewQueue')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </PageLayout>
  );
}
